import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@material-ui/core';
import SignalCellular4BarIcon from '@material-ui/icons/SignalCellular4Bar';
import WifiIcon from '@material-ui/icons/Wifi';
import BatteryFullIcon from '@material-ui/icons/BatteryFull';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';

const useTime = () => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }));
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);
  return time;
};

export const ResponsiveFrame = ({ children, isMobile, url = '' }) => {
  const time = useTime();

  return (
    <Box
      style={{
        width: isMobile ? 320 : '100%',
        height: isMobile ? 'min(620px, 100%)' : '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        boxSizing: 'border-box',
        borderRadius: isMobile ? 32 : 10,
        border: isMobile ? '9px solid #202322' : '1px solid #d9e3df',
        margin: '0 auto',
        boxShadow: isMobile
          ? '0 22px 46px -20px rgba(14, 38, 30, 0.42), 0 8px 18px rgba(14, 38, 30, 0.13)'
          : '0 12px 28px rgba(22, 54, 43, 0.1)',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Browser Header */}
      <Box
        style={{
          height: isMobile ? 0 : 40,
          opacity: isMobile ? 0 : 1,
          borderBottom: isMobile ? '0px solid #e0e0e0' : '1px solid #e0e0e0',
        }}
        bgcolor="#f0f2f5"
        display="flex"
        alignItems="center"
        px={2}
        flexShrink={0}
        overflow="hidden"
      >
        <Box display="flex" gap={1} mr={2}>
          <Box width={10} height={10} borderRadius="50%" bgcolor="#ff5f57" />
          <Box width={10} height={10} borderRadius="50%" bgcolor="#ffbd2e" />
          <Box width={10} height={10} borderRadius="50%" bgcolor="#28c940" />
        </Box>
        <Box display="flex" gap={2} mr={2} color="#5f6368">
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <RefreshIcon style={{ fontSize: 16 }} />
        </Box>
        <Box
          flex={1}
          height="28px"
          bgcolor="#fff"
          borderRadius={14}
          display="flex"
          alignItems="center"
          px={2}
          fontSize="12px"
          color="#202124"
          border="1px solid #dadce0"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {url}
        </Box>
        <Box width={24} ml={2} />
      </Box>

      {/* Phone Specifics (Notch + Status Bar) */}
      <Box
        style={{
          height: isMobile ? 46 : 0,
          opacity: isMobile ? 1 : 0,
        }}
        position="relative"
        overflow="hidden"
        bgcolor="#fff"
      >
        {/* Dynamic Island Notch */}
        <Box
          position="absolute"
          top={9}
          left="50%"
          style={{ transform: 'translateX(-50%)' }}
          width={104}
          height={30}
          bgcolor="#000"
          borderRadius={24}
          zIndex={100}
        />
        {/* Status Bar */}
        <Box
          height="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start" // Align top to avoid hitting the notch
          px={3}
          pt={1.5}
          bgcolor="#fff"
          color="#000"
        >
          <Typography variant="caption" style={{ fontWeight: 600, fontSize: 12 }}>{time || '9:41'}</Typography>
          <Box display="flex" gap={0.8} mt={0.5}>
            <SignalCellular4BarIcon style={{ fontSize: 14 }} />
            <WifiIcon style={{ fontSize: 14 }} />
            <BatteryFullIcon style={{ fontSize: 14 }} />
          </Box>
        </Box>
      </Box>

      {/* Screen Content */}
      <Box flex={1} overflow="hidden" position="relative" bgcolor="#fff">
        {children}
      </Box>

      {/* Phone Home Bar */}
      <Box
        style={{
          display: isMobile ? 'block' : 'none',
          bottom: 7,
          transform: 'translateX(-50%)',
        }}
        position="absolute"
        left="50%"
        width="112px"
        height="4px"
        bgcolor="#000"
        borderRadius={10}
        zIndex={1000}
      />
    </Box>
  );
};

export const BrowserFrame = ({ children, url = '' }) => (
  <Box
    width="100%"
    display="flex"
    flexDirection="column"
    height="100%"
    bgcolor="#fff"
    borderRadius={8}
    overflow="hidden"
    boxShadow="0 10px 30px rgba(0,0,0,0.15)"
    border="1px solid #e0e0e0"
  >
    {/* Browser Header */}
    <Box
      height="40px"
      bgcolor="#f0f2f5"
      display="flex"
      alignItems="center"
      px={2}
      borderBottom="1px solid #e0e0e0"
      flexShrink={0}
    >
      <Box display="flex" gap={1} mr={2}>
        <Box width={10} height={10} borderRadius="50%" bgcolor="#ff5f57" />
        <Box width={10} height={10} borderRadius="50%" bgcolor="#ffbd2e" />
        <Box width={10} height={10} borderRadius="50%" bgcolor="#28c940" />
      </Box>
      <Box display="flex" gap={2} mr={2} color="#5f6368">
        <ArrowBackIcon style={{ fontSize: 16 }} />
        <RefreshIcon style={{ fontSize: 16 }} />
      </Box>
      <Box
        flex={1}
        height="28px"
        bgcolor="#fff"
        borderRadius={14}
        display="flex"
        alignItems="center"
        px={2}
        fontSize="12px"
        color="#202124"
        border="1px solid #dadce0"
      >
        {url}
      </Box>
      <Box width={24} ml={2} />
    </Box>
    {/* Browser Content */}
    <Box flex={1} overflow="hidden" position="relative">
      {children}
    </Box>
  </Box>
);

export const PhoneFrame = ({ children }) => {
  const [time, setTime] = useState('');

  useEffect(() => {
    // Client-side only to avoid hydration mismatch if SSR (though this is likely CRA)
    const updateTime = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }));
    };

    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      width="375px"
      height="812px" // iPhone X dimensions approx
      bgcolor="#fff"
      borderRadius={40}
      border="12px solid #1c1c1e"
      position="relative"
      overflow="hidden"
      boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5)"
      margin="0 auto"
    >
      {/* Dynamic Island Notch */}
      <Box
        position="absolute"
        top={11}
        left="50%"
        style={{ transform: 'translateX(-50%)' }}
        width={126}
        height={37}
        bgcolor="#000"
        borderRadius={24}
        zIndex={1000}
      />

      {/* Status Bar */}
      <Box
        height="44px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={3}
        pt={1}
        bgcolor="#fff"
        color="#000"
        zIndex={900}
        position="relative"
      >
        <Typography variant="caption" style={{ fontWeight: 'bold' }}>{time || '9:41'}</Typography>
        <Box display="flex" gap={0.5}>
          <SignalCellular4BarIcon style={{ fontSize: 16 }} />
          <WifiIcon style={{ fontSize: 16 }} />
          <BatteryFullIcon style={{ fontSize: 16 }} />
        </Box>
      </Box>

      {/* Screen Content */}
      <Box height="calc(100% - 44px)" width="100%" overflow="hidden" position="relative" bgcolor="#fff">
        {children}
      </Box>

      {/* Home Bar Indicator */}
      <Box
        position="absolute"
        bottom={8}
        left="50%"
        style={{ transform: 'translateX(-50%)' }}
        width="134px"
        height="5px"
        bgcolor="#000"
        borderRadius={10}
        zIndex={1000}
      />
    </Box>
  );
};
